import { isExplicitClassic, requestScenarioIds, } from "@/composables/frequency/frequencyScenarios.js";
export const TYPE_POLICIES = [
    { id: "classic", label: "Klasik" },
    { id: "manual", label: "Tek tip" },
    { id: "constrained", label: "İzinli tipler" },
    { id: "auto", label: "Otomatik" },
];
export const AUTO_ACTIVE_TYPES_TEXT = "Tüm araçlar denenir. Tek tip ya da karışık, hangisi daha ucuzsa o gelir.";
export const SUBSET_TYPES_TEXT = "Seçilen tipler denenir. Tek tip ya da karışık, hangisi daha ucuzsa o gelir.";
export const SINGLE_TYPE_TEXT = "Yalnız seçilen tip kullanılır.";
export const CAPITAL_ZERO_WARNING = "Günlük araç sermayesi 0. Karışık planda araç sayısı artabilir.";
export const EMPTY_CATALOG_MESSAGE = "Araç kataloğunda aktif tip yok.";
export const DRIVER_COST_NOTE = "Şoför hesabı parkur süresi artı bekleme payıdır. Fazla mesai yok. Ölü km yok.";
export const STEP5_PLAN_TEXT = "Doğrulayıcı geçmedi. Adım 5 planı gösteriliyor.";
/** Backend bilgilendirme uyarıları — doğrulayıcı ret sayılmaz. */
export const INFO_UYARI_CODES = new Set(["sermaye_sifir", "tek_tip_yedek"]);
export const MISSING_CURRENT_TYPE_MESSAGE = "Mevcut araç tipi seçilmeden istek gönderilmez.";
export const PASSIVE_TYPE_MESSAGE = "Pasif tip optimizasyon seçeneği olamaz.";
export const NEED_ALLOWED_TYPE_MESSAGE = "En az bir aktif tip seçin.";
const UI_ONLY_KEYS = [
    "typePolicy",
    "vehicleTypeCode",
    "allowedTypeCodes",
    "mevcutAracTipi",
    "vehicleCatalog",
    "catalog",
    "otomatik",
    "selectedScenarios",
    "selectedMethods",
    "basitMaliyet",
];
export function policyOf(params) {
    const raw = String(params?.type_policy ?? params?.typePolicy ?? "classic")
        .trim()
        .toLowerCase();
    if (raw === "manual" || raw === "constrained" || raw === "auto")
        return raw;
    return "classic";
}
export function isCatalogPolicy(policy) {
    return policy === "manual" || policy === "constrained" || policy === "auto";
}
export function isActiveCatalogRow(row) {
    return !!row && row.active !== false;
}
export function optimizationTypeRows(catalog) {
    return (catalog || []).filter((row) => isActiveCatalogRow(row) && row.vehicle_type_code);
}
export function currentTypeRows(catalog) {
    return (catalog || []).filter((row) => row?.vehicle_type_code);
}
export function capitalsAllZero(catalog) {
    const rows = catalog || [];
    if (!rows.length)
        return false;
    return rows.every((row) => Number(row.daily_capital_cost || 0) === 0);
}
export function capitalDecisionWarning(policy, catalog) {
    if ((policy === "auto" || policy === "constrained") && capitalsAllZero(catalog)) {
        return CAPITAL_ZERO_WARNING;
    }
    return "";
}
function finiteNumber(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}
function codeOf(value) {
    return String(value ?? "").trim().toUpperCase();
}
function catalogByCode(catalog) {
    const map = new Map();
    for (const row of catalog || []) {
        const code = codeOf(row?.vehicle_type_code);
        if (code)
            map.set(code, row);
    }
    return map;
}
function stripUiKeys(body) {
    for (const key of UI_ONLY_KEYS)
        delete body[key];
}
function baselineCode(codes, catalog) {
    const active = optimizationTypeRows(catalog).map((row) => codeOf(row.vehicle_type_code));
    const pool = codes.length ? codes : active;
    if (pool.includes("SOLO"))
        return "SOLO";
    return pool[0] || "";
}
function uniqueActiveCodes(raw, catalog) {
    const byCode = catalogByCode(catalog);
    const knowCatalog = (catalog || []).length > 0;
    const requested = Array.isArray(raw) ? raw.map(codeOf).filter(Boolean) : [];
    const unique = [];
    for (const code of requested) {
        if (unique.includes(code))
            continue;
        const row = byCode.get(code);
        if (knowCatalog && (!row || !isActiveCatalogRow(row)))
            continue;
        unique.push(code);
    }
    return unique;
}
function typePickerBody(source, body, catalog) {
    delete body.seferMaliyet;
    delete body.aracMaliyet;
    const knowCatalog = (catalog || []).length > 0;
    if (source.otomatik !== false) {
        const mevcut = codeOf(source.mevcut_arac_tipi ?? source.mevcutAracTipi) ||
            baselineCode([], catalog);
        if (knowCatalog && !mevcut)
            return { ok: false, message: EMPTY_CATALOG_MESSAGE };
        body.type_policy = "auto";
        body.mevcut_arac_tipi = mevcut || "SOLO";
        delete body.vehicle_type_code;
        delete body.allowed_type_codes;
        return { ok: true, body };
    }
    const unique = uniqueActiveCodes(source.allowed_type_codes ?? source.allowedTypeCodes, catalog);
    if (!unique.length)
        return { ok: false, message: NEED_ALLOWED_TYPE_MESSAGE };
    body.mevcut_arac_tipi = baselineCode(unique, catalog);
    if (unique.length === 1) {
        body.type_policy = "manual";
        body.vehicle_type_code = unique[0];
        body.aracTipi = unique[0];
        const row = catalogByCode(catalog).get(unique[0]);
        if (row && row.capacity != null) {
            body.kapasite = finiteNumber(row.capacity, body.kapasite);
        }
        delete body.allowed_type_codes;
        return { ok: true, body };
    }
    body.type_policy = "constrained";
    body.allowed_type_codes = unique;
    delete body.vehicle_type_code;
    return { ok: true, body };
}
function attachAsimPaylari(body, source) {
    body.asim_paylari = requestScenarioIds(source.asim_paylari ?? source.selectedScenarios);
}
export function buildFrequencyRequestBody(params, catalog) {
    const source = params || {};
    const policy = policyOf(source);
    const body = { ...source };
    stripUiKeys(body);
    body.type_policy = policy;
    body.kapasite = finiteNumber(source.kapasite, 90);
    attachAsimPaylari(body, source);
    if (isExplicitClassic(source)) {
        body.type_policy = "classic";
        body.seferMaliyet = finiteNumber(source.seferMaliyet, 8);
        body.aracMaliyet = finiteNumber(source.aracMaliyet, 800);
        delete body.vehicle_type_code;
        delete body.allowed_type_codes;
        delete body.mevcut_arac_tipi;
        return { ok: true, body };
    }
    if (typeof source.otomatik === "boolean") {
        return typePickerBody(source, body, catalog);
    }
    // Tek hat analizi formu önce wire gövdesi üretir; ikinci kurulumda
    // `otomatik` yoktur. type_policy auto ise yine tip seçici yoluna gir.
    if (policy === "auto" && source.otomatik == null) {
        return typePickerBody({ ...source, otomatik: true }, body, catalog);
    }
    if (policy === "classic") {
        body.seferMaliyet = finiteNumber(source.seferMaliyet, 8);
        body.aracMaliyet = finiteNumber(source.aracMaliyet, 800);
        delete body.vehicle_type_code;
        delete body.allowed_type_codes;
        delete body.mevcut_arac_tipi;
        return { ok: true, body };
    }
    delete body.seferMaliyet;
    delete body.aracMaliyet;
    const mevcut = codeOf(source.mevcut_arac_tipi ?? source.mevcutAracTipi) ||
        baselineCode(policy === "constrained"
            ? uniqueActiveCodes(source.allowed_type_codes ?? source.allowedTypeCodes, catalog)
            : [], catalog) ||
        codeOf(source.vehicle_type_code ?? source.vehicleTypeCode ?? source.aracTipi) ||
        "SOLO";
    body.mevcut_arac_tipi = mevcut;
    const byCode = catalogByCode(catalog);
    const knowCatalog = (catalog || []).length > 0;
    if (policy === "manual") {
        const code = codeOf(source.vehicle_type_code ?? source.vehicleTypeCode ?? source.aracTipi);
        if (!code)
            return { ok: false, message: PASSIVE_TYPE_MESSAGE };
        const row = byCode.get(code);
        if (knowCatalog && (!row || !isActiveCatalogRow(row))) {
            return { ok: false, message: PASSIVE_TYPE_MESSAGE };
        }
        body.vehicle_type_code = code;
        body.aracTipi = code;
        if (row && row.capacity != null)
            body.kapasite = finiteNumber(row.capacity, body.kapasite);
        delete body.allowed_type_codes;
        return { ok: true, body };
    }
    if (policy === "constrained") {
        const raw = source.allowed_type_codes ?? source.allowedTypeCodes ?? [];
        const requested = Array.isArray(raw) ? raw.map(codeOf).filter(Boolean) : [];
        const unique = [];
        for (const code of requested) {
            if (unique.includes(code))
                continue;
            const row = byCode.get(code);
            if (knowCatalog) {
                if (!row || !isActiveCatalogRow(row))
                    continue;
            }
            unique.push(code);
        }
        if (!unique.length)
            return { ok: false, message: NEED_ALLOWED_TYPE_MESSAGE };
        body.allowed_type_codes = unique;
        delete body.vehicle_type_code;
        return { ok: true, body };
    }
    delete body.vehicle_type_code;
    delete body.allowed_type_codes;
    return { ok: true, body };
}
export function parseVehicleCatalog(body) {
    if (!body || typeof body !== "object")
        return [];
    const root = body;
    const nested = root.data && typeof root.data === "object"
        ? root.data
        : root;
    const rows = Array.isArray(nested.vehicle_catalog)
        ? nested.vehicle_catalog
        : Array.isArray(nested.vehicle_fuel_rates)
            ? nested.vehicle_fuel_rates
            : [];
    return rows
        .filter((row) => row && typeof row === "object")
        .map((row) => {
        const item = row;
        return {
            vehicle_type_code: codeOf(item.vehicle_type_code),
            display_name: String(item.display_name || item.vehicle_type_code || ""),
            capacity: finiteNumber(item.capacity, 0),
            daily_capital_cost: finiteNumber(item.daily_capital_cost, 0),
            maintenance_per_km: finiteNumber(item.maintenance_per_km, 0),
            liters_per_100km: finiteNumber(item.liters_per_100km, 0),
            active: item.active !== false,
        };
    })
        .filter((row) => row.vehicle_type_code);
}
function savingsMethodKey(methodNumber, beta) {
    if (methodNumber === 4) {
        const pct = Math.round(Number(beta) * 100);
        const allowed = pct === 0 || pct === 10 || pct === 20 || pct === 30;
        if (!allowed) {
            console.error("Tanımsız Ceder aşım payı, aşım yok kullanılıyor", beta);
            return "method4_beta0";
        }
        return `method4_beta${pct}`;
    }
    const n = methodNumber || 1;
    return `method${n}`;
}
function apiMethodKey(methodNumber, beta) {
    if (methodNumber === 4) {
        const pct = Math.round(Number(beta) * 100);
        const allowed = pct === 0 || pct === 10 || pct === 20 || pct === 30;
        if (!allowed) {
            console.error("Tanımsız Ceder aşım payı, aşım yok kullanılıyor", beta);
            return "method4_0";
        }
        return `method4_${pct}`;
    }
    return `method${methodNumber || 1}`;
}
function readMappedNumber(map, key) {
    if (!map || typeof map !== "object")
        return null;
    const record = map;
    const alias = key.startsWith("method4_beta")
        ? key.replace("method4_beta", "method4_")
        : key.replace("method4_", "method4_beta");
    const raw = record[key] ?? record[alias];
    if (raw == null || raw === "")
        return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}
function emptyCost() {
    return { yakit: null, bakim: null, sermaye: null, sofor: null };
}
function pickCost(source) {
    if (!source || typeof source !== "object")
        return null;
    const row = source;
    const nested = row.maliyet_kirilim && typeof row.maliyet_kirilim === "object"
        ? row.maliyet_kirilim
        : row;
    const keys = ["yakit", "bakim", "sermaye", "sofor"];
    if (!keys.some((key) => nested[key] != null && nested[key] !== ""))
        return null;
    const cost = emptyCost();
    for (const key of keys) {
        if (nested[key] == null || nested[key] === "")
            continue;
        const n = Number(nested[key]);
        cost[key] = Number.isFinite(n) ? n : null;
    }
    return cost;
}
function uyariList(payload, mixed) {
    const raw = payload.uyari ?? mixed?.uyari;
    if (Array.isArray(raw))
        return raw.map((item) => String(item)).filter((item) => item.trim());
    if (typeof raw === "string" && raw.trim())
        return [raw.trim()];
    return [];
}
function blockingUyari(uyari) {
    return uyari.filter((item) => !INFO_UYARI_CODES.has(String(item).trim()));
}
function tYildizForMethod(payload, savingsKey, gridKey, mixed) {
    const fromMixed = mixed?.T_yildiz;
    if (Array.isArray(fromMixed)) {
        return fromMixed.map((item) => codeOf(item)).filter(Boolean);
    }
    const map = payload.T_yildiz;
    if (map && typeof map === "object" && !Array.isArray(map)) {
        const hit = map[savingsKey] ?? map[gridKey];
        if (Array.isArray(hit))
            return hit.map((item) => codeOf(item)).filter(Boolean);
    }
    const methodMeta = payload.yontemler && typeof payload.yontemler === "object"
        ? (payload.yontemler[savingsKey] || payload.yontemler[gridKey] || null)
        : null;
    if (methodMeta && typeof methodMeta === "object") {
        const row = methodMeta;
        if (Array.isArray(row.T_yildiz)) {
            return row.T_yildiz.map((item) => codeOf(item)).filter(Boolean);
        }
    }
    return [];
}
function mixedForMethod(payload, savingsKey, gridKey) {
    const map = payload.karisik_planlar;
    if (map && typeof map === "object" && !Array.isArray(map)) {
        const hit = map[savingsKey] ?? map[gridKey];
        if (hit && typeof hit === "object")
            return hit;
        return null;
    }
    return mixedRecord(payload);
}
function mixedRecord(payload) {
    const raw = payload.karisik_plan ?? payload.karisik;
    if (!raw || typeof raw !== "object")
        return null;
    return raw;
}
function typeCounts(source) {
    if (!source || typeof source !== "object")
        return null;
    const row = source;
    const map = row.arac_sayisi_tiplere;
    if (!map || typeof map !== "object" || Array.isArray(map))
        return null;
    const counts = Object.entries(map)
        .map(([code, count]) => ({ code: codeOf(code), count: Number(count) }))
        .filter((item) => item.code && Number.isFinite(item.count));
    return counts.length ? counts : null;
}
export function extractPolicyPayload(body) {
    if (!body || typeof body !== "object")
        return null;
    const root = body;
    const nested = root.sonuclar && typeof root.sonuclar === "object" ? root.sonuclar : null;
    const source = root.type_policy || root.saf_tip_maliyetleri || root.karisik_plan || root.uyari || root.T_yildiz
        ? root
        : nested;
    if (!source)
        return null;
    const policy = String(source.type_policy || "");
    const hasCatalog = isCatalogPolicy(policy) ||
        Array.isArray(source.saf_tip_maliyetleri) ||
        !!source.karisik_plan ||
        !!source.karisik ||
        !!source.karisik_planlar ||
        !!source.T_yildiz ||
        !!source.uyari;
    if (!hasCatalog)
        return null;
    return {
        type_policy: policy,
        mevcut_arac_tipi: source.mevcut_arac_tipi ? String(source.mevcut_arac_tipi) : "",
        tasarruf_frekans: (source.tasarruf_frekans || undefined),
        tasarruf_tip: (source.tasarruf_tip || undefined),
        saf_tip_maliyetleri: source.saf_tip_maliyetleri,
        yontemler: (source.yontemler || source.saf_tip_yontemleri),
        T_yildiz: (source.T_yildiz || undefined),
        uyari: source.uyari,
        karisik_plan: (source.karisik_plan || source.karisik || null),
        karisik_planlar: (source.karisik_planlar || undefined),
        maliyet_kirilim: source.maliyet_kirilim,
        dogrulandi: source.dogrulandi,
    };
}
export function readFrequencyOutcome(payload, query = {}) {
    if (!payload)
        return null;
    const methodNumber = query.methodNumber ?? 1;
    const beta = query.beta ?? 0.3;
    const savingsKey = savingsMethodKey(methodNumber, beta);
    const gridKey = apiMethodKey(methodNumber, beta);
    const mixed = mixedForMethod(payload, savingsKey, gridKey);
    const uyari = uyariList(payload, mixed);
    const engel = blockingUyari(uyari);
    const failed = engel.length > 0 || payload.dogrulandi === false || mixed?.dogrulandi === false;
    const showMixed = !!mixed && !failed;
    const tYildiz = tYildizForMethod(payload, savingsKey, gridKey, mixed);
    const pureRows = (Array.isArray(payload.saf_tip_maliyetleri) ? payload.saf_tip_maliyetleri : [])
        .filter((row) => row && typeof row === "object")
        .map((row) => {
        const item = row;
        return {
            code: codeOf(item.vehicle_type_code),
            kapasite: item.kapasite == null ? null : finiteNumber(item.kapasite, 0),
            maliyet: readMappedNumber(item.maliyetler, savingsKey),
            baseline: item.baseline === true,
        };
    })
        .filter((row) => row.code);
    const methodMeta = payload.yontemler && typeof payload.yontemler === "object"
        ? (payload.yontemler[savingsKey] ||
            payload.yontemler[gridKey] ||
            null)
        : null;
    const optimizationMeta = query.optimization && typeof query.optimization === "object"
        ? query.optimization[gridKey]
        : null;
    let breakdown = [];
    if (showMixed && mixed) {
        const plan = mixed.plan || mixed;
        breakdown = typeCounts(plan) || typeCounts(mixed) || [];
    }
    if (!breakdown.length) {
        const fromMethod = typeCounts(methodMeta) || typeCounts(optimizationMeta);
        if (fromMethod)
            breakdown = fromMethod;
    }
    if (!breakdown.length && methodMeta && typeof methodMeta === "object") {
        const meta = methodMeta;
        const code = codeOf(meta.vehicle_type_code);
        const countSource = optimizationMeta && typeof optimizationMeta === "object"
            ? optimizationMeta.arac_sayisi
            : meta.arac_sayisi;
        if (code)
            breakdown = [{ code, count: finiteNumber(countSource, 0) }];
    }
    let cost = emptyCost();
    if (showMixed && mixed) {
        cost = pickCost(mixed.plan) || pickCost(mixed) || emptyCost();
    }
    if (cost.yakit == null && cost.bakim == null && cost.sermaye == null && cost.sofor == null) {
        cost = pickCost(methodMeta) || pickCost(payload.maliyet_kirilim) || pickCost(optimizationMeta) || emptyCost();
    }
    const show = isCatalogPolicy(String(payload.type_policy || "")) ||
        pureRows.length > 0 ||
        showMixed ||
        failed;
    if (!show)
        return null;
    return {
        show,
        typePolicy: String(payload.type_policy || ""),
        mevcutAracTipi: String(payload.mevcut_arac_tipi || ""),
        pureRows,
        breakdown,
        tYildiz,
        cost,
        tasarrufFrekans: readMappedNumber(payload.tasarruf_frekans, savingsKey),
        tasarrufTip: readMappedNumber(payload.tasarruf_tip, savingsKey),
        showMixed,
        validatorFailed: failed,
        step5: failed,
        uyari: failed ? engel : uyari,
        note: DRIVER_COST_NOTE,
    };
}
